import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, User, Phone, Mail, MessageSquare, Edit2, Check } from 'lucide-react';
import { PatientLeadProgressTracker } from '../intake/PatientLeadProgressTracker';
import { PatientLeadStatusBadge } from './PatientLeadStatusBadge';
import { formatPacificDateTime } from '../../utils/dateTime';
import { RichTextEditor } from '../intake/RichTextEditor';
import { supabase } from '../../lib/supabase';

interface PatientLead {
  id: string;
  first_name: string;
  last_name: string;
  phone_number: string | null;
  email: string | null;
  comments: string | null;
  mapped_patient_id: string | null;
  status: string;
  created_at: string;
  created_by: string;
  progress_percentage?: number;
}

interface PatientLeadDetailProps {
  lead: PatientLead;
  onClose: () => void;
  onEdit: (lead: PatientLead) => void;
  onProgressUpdate: () => void;
}

export function PatientLeadDetail({ lead, onClose, onEdit, onProgressUpdate }: PatientLeadDetailProps) {
  const [editingComments, setEditingComments] = useState(false);
  const [comments, setComments] = useState(lead.comments || '');
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  // Animation effect when opening
  useEffect(() => {
    // Small delay to allow the modal to render before animating
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 50);
    
    return () => clearTimeout(timer);
  }, []);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  const handleStepComplete = () => {
    onProgressUpdate();
  };

  const handleIntakeComplete = () => {
    onProgressUpdate();
  };

  const handleCommentsChange = (newComments: string) => {
    setComments(newComments);
  };

  const saveComments = async () => {
    if (comments === lead.comments) return;
    
    try {
      setIsSaving(true);
      
      const { error } = await supabase
        .from('patient_leads')
        .update({ comments })
        .eq('id', lead.id);
        
      if (error) throw error;
      
      // Show success indicator briefly
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
      
      // Update the lead object with new comments
      lead.comments = comments;
    } catch (error) {
      console.error('Error saving comments:', error);
    } finally {
      setIsSaving(false);
      setEditingComments(false);
    }
  };

  // Function to render comments with HTML formatting
  const renderComments = () => {
    if (!lead.comments) return null;
    
    // Check if the comments appear to contain HTML
    const containsHtml = /<[a-z][\s\S]*>/i.test(lead.comments);
    
    if (containsHtml) {
      return <div dangerouslySetInnerHTML={{ __html: lead.comments }} />;
    } else {
      // If it's plain text, preserve line breaks
      return (
        <div>
          {lead.comments.split('\n').map((line, i) => (
            <React.Fragment key={i}>
              {line}
              {i < lead.comments!.split('\n').length - 1 && <br />}
            </React.Fragment>
          ))}
        </div>
      );
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-0 flex items-center justify-center p-4 z-50 transition-all duration-300 ease-in-out"
      style={{ 
        backgroundColor: isVisible ? 'rgba(0, 0, 0, 0.75)' : 'rgba(0, 0, 0, 0)',
        backdropFilter: isVisible ? 'blur(2px)' : 'blur(0px)'
      }}
    >
      <div 
        ref={modalRef}
        className={`bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col transform transition-all duration-300 ease-out ${
          isVisible ? 'scale-100 opacity-100 translate-y-0' : 'scale-95 opacity-0 translate-y-8'
        }`}
      >
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
          <div className="flex items-center">
            <button
              onClick={onClose}
              className="mr-4 text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h3 className="text-lg font-medium text-gray-900">
              {lead.first_name} {lead.last_name}
            </h3>
            <div className="ml-4">
              <PatientLeadStatusBadge status={lead.status} />
            </div>
          </div>
          <button
            onClick={() => onEdit(lead)}
            className="text-blue-600 hover:text-blue-900"
          >
            <Edit2 className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
              <h4 className="text-sm font-medium text-gray-500 mb-2">Contact Information</h4>
              <div className="space-y-2">
                <div className="flex items-center">
                  <User className="h-5 w-5 text-gray-400 mr-2" />
                  <span className="text-gray-900">{lead.first_name} {lead.last_name}</span>
                </div>
                {lead.phone_number && (
                  <div className="flex items-center">
                    <Phone className="h-5 w-5 text-gray-400 mr-2" />
                    <span className="text-gray-900">{lead.phone_number}</span>
                  </div>
                )}
                {lead.email && (
                  <div className="flex items-center">
                    <Mail className="h-5 w-5 text-gray-400 mr-2" />
                    <span className="text-gray-900">{lead.email}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
              <h4 className="text-sm font-medium text-gray-500 mb-2">Status</h4>
              <div className="space-y-2">
                <div>
                  <span className="text-sm text-gray-500">Current Status:</span>
                  <div className="mt-1">
                    <PatientLeadStatusBadge status={lead.status} />
                  </div>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Created:</span>
                  <div className="text-gray-900">
                    {formatPacificDateTime(lead.created_at)}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
              <h4 className="text-sm font-medium text-gray-500 mb-2">Progress</h4>
              <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
                <div 
                  className="bg-green-600 h-2.5 rounded-full" 
                  style={{ width: `${lead.progress_percentage || 0}%` }}
                ></div>
              </div>
              <span className="text-sm text-gray-900">
                {lead.progress_percentage || 0}% Complete
              </span>
            </div>
          </div>

          {/* Comments Section */}
          <div className="bg-white p-4 rounded-lg shadow border border-gray-200 mb-8">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium text-gray-500 flex items-center">
                <MessageSquare className="h-5 w-5 text-gray-400 mr-2" />
                Comments
              </h4>
              <div className="flex items-center">
                {editingComments ? (
                  <div className="flex items-center">
                    {isSaving && <span className="text-xs text-gray-500 mr-2">Saving...</span>}
                    {saveSuccess && (
                      <span className="text-xs text-green-500 mr-2 flex items-center">
                        <Check className="h-3 w-3 mr-1" />
                        Saved
                      </span>
                    )}
                    <button 
                      onClick={saveComments}
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      Done
                    </button>
                  </div>
                ) : (
                  <button 
                    onClick={() => setEditingComments(true)}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    Edit
                  </button>
                )}
              </div>
            </div>
            
            {editingComments ? (
              <RichTextEditor 
                content={comments} 
                onChange={handleCommentsChange}
                onBlur={saveComments}
                className="min-h-[150px]"
              />
            ) : (
              <div className="text-gray-900 prose prose-sm max-w-none">
                {renderComments()}
              </div>
            )}
          </div>

          <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
            <h4 className="text-lg font-medium text-gray-900 mb-4">Intake Progress</h4>
            <PatientLeadProgressTracker 
              patientLeadId={lead.id} 
              onStepComplete={handleStepComplete}
              onComplete={handleIntakeComplete}
            />
          </div>
        </div>
      </div>
    </div>
  );
}